import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchServiceProfile } from '@/services/serviceProfiles';
import { VisualTokenEditor } from '@/components/design-system/VisualTokenEditor';

/**
 * Read-only design system token visualization page.
 *
 * This page is designed to be captured by Puppeteer + Figma's capture.js script.
 * It renders all DESIGN.md tokens visually (colors, typography, spacing, etc.)
 * at 1440px wide with no navigation chrome.
 */
export function DesignSystemPreview() {
  const { serviceProfileId } = useParams<{ serviceProfileId: string }>();

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['serviceProfile', serviceProfileId],
    queryFn: () => fetchServiceProfile(serviceProfileId!),
    enabled: !!serviceProfileId,
  });

  if (isLoading) {
    return (
      <div style={containerStyle}>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Loading design system...</p>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div style={containerStyle}>
        <p style={{ color: 'var(--color-error)', fontSize: '14px' }}>
          Failed to load service profile{serviceProfileId ? ` (${serviceProfileId})` : ''}.
        </p>
      </div>
    );
  }

  const markdown = profile.designSystemMd || '';

  if (!markdown) {
    return (
      <div style={containerStyle}>
        <h1 style={titleStyle}>{profile.displayName || profile.repo} — Design System</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>No design system defined for this service.</p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>{profile.displayName || profile.repo} — Design System</h1>
      <p style={subtitleStyle}>
        Design tokens exported for Figma review. This page is a visual reference — edit in Figma.
      </p>
      <div style={editorWrapperStyle}>
        <VisualTokenEditor markdown={markdown} onMarkdownChange={() => {}} />
      </div>
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  width: '1440px',
  maxWidth: '1440px',
  margin: '0 auto',
  padding: '40px 60px',
  backgroundColor: 'var(--color-card-surface)',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const titleStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 700,
  color: 'var(--text-primary)',
  marginBottom: '8px',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '13px',
  color: 'var(--text-muted)',
  marginBottom: '32px',
};

const editorWrapperStyle: React.CSSProperties = {
  pointerEvents: 'none',
};
